import {InjectionToken, LOCALE_ID, NgModule} from "@angular/core";
import {MissingTranslationHandler, TranslateCompiler, TranslateFakeLoader, TranslateLoader, TranslateModule, TranslateParser} from "@ngx-translate/core";
import {EnkleKrøllParenteserForParametereParser, ManglendeOversettelserHandler, OversettelsesCompiler} from "felles-ng2/tjenester/i18n/translateHandlere";
import {Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {OversettelserRegister} from "felles-ng2/tjenester/i18n/OversettelserRegister";
import {GjeldendeLokale} from "./GjeldendeLokale";
import {GjeldendeTidssone} from "./GjeldendeTidssone";
import {TemporalFormatering} from "../TemporalFormatering";
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from "@angular/material/core";
import {MAT_PLAINDATE_FORMATS, PlainDateDateAdapter} from "felles-ng2/infrastruktur/material/PlainDateDateAdapter";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MatStepperIntl} from "@angular/material/stepper";
import {MatStepperOversettelse} from "felles-ng2/tjenester/i18n/MatStepperOversettelse";
import {EnumOversettelser} from "felles-ng2/tjenester/i18n/EnumOversettelser";
import {DatoPeriodeFormatering} from "felles-ng2/tjenester/DatoPeriodeFormatering";
import {DatoPeriodeUtenTidFormatering} from "felles-ng2/tjenester/DatoPeriodeUtenTidFormatering";
import {MatPaginatorIntl} from "@angular/material/paginator";
import {MatPaginatorOversettelse} from "felles-ng2/tjenester/i18n/MatPaginatorOversettelse";
import {Språk} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";

const debug = initDebugger('InternasjonaliseringModul');

/** Standard språk benyttet i ngx-translate */
export const StandardSpråk = Språk.norsk_bokmål

/**
 * Modul for registrering og oppsett av internasjonaliseringsrelaterte tjenester.
 *
 * Merk at denne modulen skal IKKE importeres i de enkelte komponenter som bruker feks translate-pipen.
 * Til det formålet må komponenten importere TranslateModule
 */
@NgModule({
    imports: [
        //oppsett av https://github.com/ngx-translate/core
        TranslateModule.forRoot({
              defaultLanguage: StandardSpråk,
              //alle oversettelser blir "dekortert" med [] når vi er i Miljø.test
              compiler: {provide: TranslateCompiler, useClass: OversettelsesCompiler},
              //interpolere {} istedetfor gettext sin {{}} som nå angular bruker selv
              parser: {provide: TranslateParser, useClass: EnkleKrøllParenteserForParametereParser},
              //skriver ut '[ MANGLER: <nøkkel> ]' dersom ingen oversettelse er registrert når vi er i Miljø.test
              missingTranslationHandler: {provide: MissingTranslationHandler, useClass: ManglendeOversettelserHandler},
              //oversettelser lastet via OversettelserRegister
              loader: {provide: TranslateLoader, useClass: TranslateFakeLoader}
          }
        )
    ],
    providers: [
        //tjenester knyttet til oversettelser
        // Vi kan overstyre Oversettelser ved behov fra eget config-objekt
        Oversettelser,
        OversettelserRegister,
        EnumOversettelser,

        //tjenester knyttet til lokale
        GjeldendeLokale,

        //tjenester knyttet til tidssone
        GjeldendeTidssone,
        TemporalFormatering,
        DatoPeriodeFormatering,
        DatoPeriodeUtenTidFormatering,

        {provide: MatStepperIntl, useClass: MatStepperOversettelse},
        {provide: MatPaginatorIntl, useClass: MatPaginatorOversettelse},
        {provide: DateAdapter, useClass: PlainDateDateAdapter}, // ved introduksjon av flere temporal typer må det skrives en adapter per type..
        {provide: PlainDateDateAdapter, useExisting: DateAdapter},
        // LOCALE_ID er Angular sin standard angivelse av locale, Material ser til LOCALE_ID,
        // men det spiller ikke helt igjennom i test-oppsett, setter begge her.
        //@todo hvordan får vi oppdatert denne til korrekt verdig - når denne koden evalueres er vel neppe bruker autentisert?
        gjeldendeLocaleFactory(LOCALE_ID),
        gjeldendeLocaleFactory(MAT_DATE_LOCALE),
        // Sammen med PlainDateDateAdapter styrer denne formatering av datoer f.eks. i datepicker
        {provide: MAT_DATE_FORMATS, useValue: MAT_PLAINDATE_FORMATS},
        // Angir hvordan Angular sin DatePipe skal formatere datoer og gjør den sensitiv for "tidssone..",
        // Antakelig kan denne ikke brukes pga tidssonefeil ? Se kommentar
        // {provide: DATE_PIPE_DEFAULT_OPTIONS, useFactory: (tidssone: Tidssonevalg, kontekst: Kontekst) => {
        //         // Det er mulig vi ikke kan bruke datePipe pga. problemer med sommer/vintertid?
        //         // - https://github.com/angular/angular/issues/48279
        //         // - https://github.com/angular/angular/issues/49540
        //
        //         // Annet aspekt er at tidsverdien ikke blir oppdatert i pipe etter at tidssone er satt på nytt
        //         // fører til at vi ev. må relaste deler av komponenttreet e.l., i praksis må vi da alltid ha et aktiv standpunkt til at vi lytter etter endringer
        //
        //         // Må hente fra server når vi vet at brukeren har autentisert -
        //         // hvordan vet vi det? Kan vi anta det så lenge vi er i en verden hvor alle ng2-komponenter er i en innlogget kontekst?
        //         tidssone.oppdaterFraKontekst(kontekst)
        //         return {timezone: tidssone.aktivTidssoneOffsetForNåtid(), dateFormat: 'MM.dd.YYYY'}
        //     }, deps: [Tidssonevalg, Kontekst]}
    ]
})
export class InternasjonaliseringModul {

    constructor(oversettelserRegister: OversettelserRegister) {
        oversettelserRegister.lastOversettelser();
    }

}

function gjeldendeLocaleFactory(token: InjectionToken<string>) {
    return {
        provide: token,
        useFactory: (gjeldendeLokale: GjeldendeLokale) => {
            debug(`gjeldendeLocaleFactory for ${token} kalt`)
            return gjeldendeLokale.brukersLokale().somLocaleId()
        },
        deps: [GjeldendeLokale]
    }
}
